// eslint-disable-next-line @heidi/rules/must-lazy-load-modals -- pre-existing eager modal, surfaced when its drawer import moved to the door; lazy-load follow-up tracked separately
import { JurisdictionSelect } from '@/components/ui/jurisdiction-select/JurisdictionSelect';
import { IS_JURISDICTION_IN_ONBOARDING_ENABLED } from '@/features/flags/legacy-flags';
import { waitUntil } from '@/helpers/waitUntil';
import { useIsWidgetAuthFlow } from '@/hooks/integrations/useIsWidgetAuthFlow';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useMutateUpdateUserRegion } from '@/hooks/mutation/useMutateUpdateUserRegion';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { getCurrentUser, useQueryUser } from '@/hooks/query/useQueryUser';
import { demoBindOnboardingInfo } from '@/lib/analytics/scribe/auth';
import type { AuthSessionMetadata } from '@/lib/auth';
import { cn } from '@/lib/cn';
import {
  buildRegionalUrl,
  getHostForRegion,
  needsRegionRedirect,
} from '@/lib/region-routing';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyCaptionBold, TypographyP2 } from '@heidi/design-system/typography';
import {
  clinicianTypeAtom,
  countryAtom,
  importedTemplateIdAtom,
  jurisdictionAtom,
  otherClinicianTypeAtom,
  signupSourceAtom,
} from '@heidi/growth/data';
import { hasCompletedOnboardingForWidgetAtom } from '@heidi/integrations/ehr/data/atoms';
import { useTranslations } from '@heidi/runtime/i18n';
import { SignupSource } from '@heidi/schema/ml-scribe';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useIsMobile } from '@heidi/utils';
import { countries, type CountryType } from '@heidi/utils/countries';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import ClinicianTypeSelect, {
  OTHER_SPECIALTY_ID,
} from '../../ui/select/clinician-type-select';
import CountrySelect from '../../ui/select/country-select';
interface Props {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

function LegacyOnboardingRequiredUserDetailsModal(props: Props) {
  const router = useRouter();
  const { isOpen, setOpen } = props;
  const [country, setCountry] = useAtom(countryAtom);
  const [clinicianType, setClinicianType] = useAtom(clinicianTypeAtom);
  const [otherClinicianType, setOtherClinicianType] = useAtom(otherClinicianTypeAtom);
  const [jurisdiction, setJurisdiction] = useAtom(jurisdictionAtom);
  const { mutate: mutateUpdateUser, isPending: isUserPending } = useMutateUpdateUser();
  const { mutateAsync: mutateUpdateUserRegion, isPending: isRegionPending } =
    useMutateUpdateUserRegion();
  const isLoading = isUserPending || isRegionPending;
  const importedTemplateId = useAtomValue(importedTemplateIdAtom);
  const { data: user } = useQueryUser();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);
  const { t } = useTranslations();
  const hasUpdatedUser = useRef(false);
  const hasUpdatedRegion = useRef(false);
  const hasSeededCountry = useRef(false);
  const hasSeededSpecialty = useRef(false);

  // Preselect the form from the user's saved values instead of the geo-guessed
  // default (`countryAtom` defaults to `getCountry()`, `clinicianTypeAtom` to
  // undefined). On-prem the appliance country is stamped on team-join, so a
  // user reaching this modal often already has a country/specialty that should
  // show as selected. Seed once each; only when the user has a value (never
  // clobber an in-progress selection or overwrite with empty).
  useEffect(() => {
    if (hasSeededCountry.current || !user?.country) return;
    const match = countries.find((c) => c.name === user.country);
    if (match) {
      setCountry(match);
      hasSeededCountry.current = true;
    }
  }, [user?.country, setCountry]);

  useEffect(() => {
    if (hasSeededSpecialty.current || !user) return;
    if (user.specialty_id) {
      if (!specialties) return;
      const match = specialties.find((s) => s.id === user.specialty_id);
      if (match) {
        setClinicianType(match);
        hasSeededSpecialty.current = true;
      }
    } else if (user.other_specialty) {
      setClinicianType({
        id: OTHER_SPECIALTY_ID,
        name: 'Other',
        group: { id: 'other', name: 'Other' },
      });
      setOtherClinicianType(user.other_specialty);
      hasSeededSpecialty.current = true;
    }
  }, [user, specialties, setClinicianType, setOtherClinicianType]);
  const setHasCompletedOnboardingForWidget = useSetAtom(
    hasCompletedOnboardingForWidgetAtom
  );
  const { isWidgetAuthFlow } = useIsWidgetAuthFlow();
  const signupSource = useAtomValue(signupSourceAtom);

  const shouldRedirectToScribe =
    isEmpty(importedTemplateId) &&
    !isWidgetAuthFlow &&
    signupSource !== SignupSource.evidence;

  // Use country from atom if set, otherwise fall back to user's saved country
  const effectiveCountryName = country?.name ?? user?.country;

  const updateUserRequiredDetails = async () => {
    let regionSession: AuthSessionMetadata | null | undefined;
    // Region assignment is intentionally not gated on `isEmpty(user?.country)`.
    // This modal only mounts when the user has no Kinde region (see
    // `shouldShowLegacyOnboardingDetailsModal` in GlobalModalsWrapper), so any
    // submission here is for a user missing a region — including users who already
    // have a saved country but missed the region backfill. The actual subdomain
    // redirect is still gated by `needsRegionRedirect` below, so re-assigning a
    // region that matches the current host is a routing no-op.
    if (country?.name && !hasUpdatedRegion.current) {
      const region = countriesToRegion[country.name];
      if (region) {
        regionSession = await mutateUpdateUserRegion(region);
        hasUpdatedRegion.current = true;
      }
    }
    await waitUntil(() => {
      const currentUser = getCurrentUser();
      return !isEmpty(currentUser);
    });
    if (!hasUpdatedUser.current) {
      mutateUpdateUser(
        {
          country: country?.name,
          other_specialty: otherClinicianType,
          specialty_id:
            clinicianType?.id === OTHER_SPECIALTY_ID ? undefined : clinicianType?.id,
          ...(jurisdiction && { jurisdiction }),
          ...(signupSource && { signup_source: signupSource }),
        },
        {
          onSuccess: () => {
            demoBindOnboardingInfo();
            if (isWidgetAuthFlow) {
              setHasCompletedOnboardingForWidget(true);
            }
          },
        }
      );
      hasUpdatedUser.current = true;
    }

    const refreshedRegion = regionSession?.region;
    if (refreshedRegion && needsRegionRedirect(window.location.host, refreshedRegion)) {
      window.location.assign(
        buildRegionalUrl(getHostForRegion(refreshedRegion), '/scribe')
      );
      return;
    }
    if (shouldRedirectToScribe) {
      void router.push('/scribe');
    } else if (signupSource === SignupSource.evidence) {
      void router.push('/scribe/chat');
    }
  };

  const isMobile = useIsMobile();

  // Reset the specialty when the user picks a different country so a
  // selection made under the old country's scoped list never persists. Lives
  // only in the picker's change handler — the seed effects above write via the
  // raw setCountry, so seeding never clears an in-progress selection.
  const handleCountryChange = (newCountry: CountryType) => {
    if (newCountry.countryCode !== country?.countryCode) {
      setClinicianType(undefined);
      setOtherClinicianType(undefined);
    }
    setCountry(newCountry);
  };

  const specialtyField = (
    <div key="specialty" className="flex w-full flex-col">
      <TypographyP2 className="font-semibold">{t('setupPage.specialty')}</TypographyP2>
      <ClinicianTypeSelect
        selectedSpecialty={clinicianType}
        setSelectedSpecialty={setClinicianType}
        otherSpecialty={otherClinicianType}
        setOtherSpecialty={setOtherClinicianType}
        countryCode={country?.countryCode}
        disabled={!country}
      />
    </div>
  );

  const countryField = (
    <div key="country" className="flex w-full flex-col">
      <TypographyP2 className="font-semibold">{t('setupPage.country')}</TypographyP2>
      <CountrySelect value={country} setValue={handleCountryChange} />
    </div>
  );

  const orderedSelectFields = [countryField, specialtyField];

  return (
    <DialogDrawer open={isOpen} onOpenChange={setOpen} dismissible={false}>
      <DialogDrawerContent
        dismissible={false}
        className={cn(
          'my-5 flex min-h-fit flex-col justify-center bg-surface px-7 md:min-w-150',
          isMobile && '!h-fit'
        )}
        overlayClassName="backdrop-blur-xs"
      >
        <div className="my-4 flex w-full flex-col items-center justify-center gap-2">
          <HeidiLogoMark className="size-8 self-center" artwork="standard" />
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <DialogDrawerTitle>{t('setupPage.title')}</DialogDrawerTitle>
            <TypographyCaptionBold className="text-center leading-6 text-text-secondary">
              {t('setupPage.descriptionPersonal')}
            </TypographyCaptionBold>
          </div>
          {orderedSelectFields}
          {IS_JURISDICTION_IN_ONBOARDING_ENABLED() && effectiveCountryName && (
            <div className="flex w-full flex-col">
              <TypographyP2 className="font-semibold">
                {t('chat.onboarding.stateRegion')}
              </TypographyP2>
              <JurisdictionSelect
                countryName={effectiveCountryName}
                value={jurisdiction ?? null}
                onChange={setJurisdiction}
              />
            </div>
          )}
          <TypographyP2 className="text-text-secondary">
            <Trans
              i18nKey="setupPage.dataSecurityLink"
              defaults="Learn more about <link>our approach to data security and privacy</link>"
              components={{
                link: (
                  <a
                    className="cursor-pointer font-semibold text-text-link"
                    target="_blank"
                    href="https://heidihealth.com/safety/"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </TypographyP2>
          <Button
            size="lg"
            disableAutomaticSvgSizing
            className="mt-5 w-full"
            disabled={isLoading || !country || !clinicianType}
            onClick={() => void updateUserRequiredDetails()}
          >
            {isLoading && <Loader className="mr-3 text-text-primary-invert" size={22} />}
            {t('setupPage.customizeExperience')}
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}

export default LegacyOnboardingRequiredUserDetailsModal;
